import { useDroppable } from "@dnd-kit/core";
import { faPlus } from "@fortawesome/free-solid-svg-icons/faPlus";
import React from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraIcon } from "../../../components/AtiraIcon";
import { Button } from "../../../components/Button";
import { Text } from "../../../components/Text";
import i18n from "../../../i18n";
import { KanbanAutomationRule } from "../../../model/kanban/KanbanAutomationRule";
import { KanbanColumn } from "../../../model/kanban/KanbanColumn";
import { KanbanTemplates } from "../../../model/kanban/types/KanbanTemplates.enum";
import { AppLangs } from "../../../model/shared/enum/AppLangs.enum";
import { User } from "../../../model/user/User";
import { Rounded } from "../../../theme/Rounded";
import { Shadow } from "../../../theme/Shadow";
import { Spacing } from "../../../theme/Spacing";
import { getTranslatedKanbanColumnName } from "../KanbanUtils";
import { DraggableKanbanAutomationRuleCard } from "./KanbanAutomationRuleCard";

const StyledLane = styled.div<{ $rtl: boolean; $dropTarget: boolean }>`
  direction: ${({ $rtl }) => ($rtl ? "rtl" : "ltr")};
  display: flex;
  flex-direction: column;
  flex: 1 0 min(22rem, 88%);
  max-width: 28rem;
  height: 100%;
  overflow: hidden;
  border-radius: ${Rounded.lg};
  border: 1px solid
    ${({ theme, $dropTarget }) => ($dropTarget ? theme.main : theme.darkSub)};
  background-color: ${({ theme }) => theme.sub};
  box-shadow: ${({ theme, $dropTarget }) =>
    $dropTarget ? `0 0 0 2px ${theme.main}` : Shadow.SM};
  transition:
    border-color 0.2s,
    box-shadow 0.2s;

  &:hover .kanban-automation-lane-add,
  &:focus-within .kanban-automation-lane-add {
    opacity: 1;
  }

  @media (hover: none) {
    .kanban-automation-lane-add {
      opacity: 1;
    }
  }
`;

const LaneHeader = styled.div<{ $isDefault: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${Spacing.s};
  padding: ${Spacing.m};
  background-color: ${({ $isDefault, theme }) =>
    $isDefault ? theme.darkSub : theme.darkMain};
  color: ${({ $isDefault, theme }) =>
    $isDefault ? theme.textColor : theme.white};
  border-bottom: 1px solid ${({ theme }) => theme.darkSub};

  * {
    color: inherit;
  }
`;

const LaneTitle = styled.div`
  flex: 1;
  min-width: 0;
`;

const RulesCount = styled.div`
  flex-shrink: 0;
  min-width: 1.5rem;
  padding: 0 ${Spacing.s};
  border-radius: ${Rounded.pill};
  border: 1px solid currentColor;
`;

const LaneBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${Spacing.m};
  min-height: 0;
  padding: ${Spacing.m};
  overflow-y: auto;
  background-color: ${({ theme }) => theme.sub};

  scrollbar-color: ${({ theme }) => `${theme.main} ${theme.sub}`};
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.sub};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.main};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.lightMain};
  }
`;

const EmptyLane = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.m};
`;

const AddRuleButton = styled(Button)`
  flex-shrink: 0;
  width: 100%;
  height: 3.5rem;
  margin: 0;
  border-radius: ${Rounded.none};
  background-color: ${({ theme }) => theme.sub};
  color: ${({ theme }) => theme.main};
  border-top: 1px solid ${({ theme }) => theme.darkSub};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.xs};
  opacity: 0;

  &:hover {
    background-color: ${({ theme }) => theme.lightSub};
    color: ${({ theme }) => theme.main};
  }

  &:focus-visible {
    opacity: 1;
    outline: 2px solid ${({ theme }) => theme.main};
    outline-offset: -2px;
  }

  p {
    color: inherit;
  }
`;

type KanbanAutomationLaneProps = {
  column: KanbanColumn;
  template?: KanbanTemplates;
  rules: KanbanAutomationRule[];
  usersById: Map<string, User>;
  /** Rules with a move or change still in flight. */
  pendingRuleIds: string[];
  onAdd: (column: KanbanColumn) => void;
  onEdit: (rule: KanbanAutomationRule) => void;
  onDelete: (rule: KanbanAutomationRule) => void;
};

/** One column of the automation board: only its rules, no deals. */
export const KanbanAutomationLane: React.FC<KanbanAutomationLaneProps> = ({
  column,
  template,
  rules,
  usersById,
  pendingRuleIds,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  const { setNodeRef, isOver, active } = useDroppable({ id: column._id });

  const name = getTranslatedKanbanColumnName(column.name, template);
  // Hovering the lane the dragged rule already sits in is not a move.
  const isDropTarget = isOver && active?.data.current?.columnId !== column._id;

  return (
    <StyledLane
      ref={setNodeRef}
      role="group"
      aria-label={name}
      $rtl={i18n.language === AppLangs.AR}
      $dropTarget={isDropTarget}
    >
      <LaneHeader $isDefault={column.default === true}>
        <LaneTitle>
          <Text fontSize="l" fontWeight={700} cutText>
            {name}
          </Text>
        </LaneTitle>

        <RulesCount
          aria-label={t("kanban.automation.lane.rulesTotal", {
            total: rules.length,
          })}
        >
          <Text fontSize="s" align="center">
            {rules.length}
          </Text>
        </RulesCount>
      </LaneHeader>

      <LaneBody>
        {rules.length ? (
          rules.map((rule) => (
            <DraggableKanbanAutomationRuleCard
              key={rule._id}
              rule={rule}
              columnId={column._id}
              columnName={name || ""}
              usersById={usersById}
              pending={pendingRuleIds.includes(rule._id)}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))
        ) : (
          <EmptyLane>
            <Text color="gray" align="center">
              {t("kanban.automation.lane.empty")}
            </Text>
          </EmptyLane>
        )}
      </LaneBody>

      <AddRuleButton
        className="kanban-automation-lane-add"
        onClick={() => onAdd(column)}
      >
        <AtiraIcon icon={faPlus} size="1x" />
        <Text fontSize="m">{t("kanban.automation.lane.add")}</Text>
      </AddRuleButton>
    </StyledLane>
  );
};
